/* Live price plumbing for the Day Trading suite — Polygon.io backed.

   Replaces the old Yahoo Finance + multi-proxy chain with direct calls to
   our backend /api/stock/* endpoints, which proxy Polygon real-time data
   server-side. The frontend never talks to Polygon (or any third-party
   data source) directly any more.

   Exports:
     - fetchPrice(ticker)            single real-time price
     - fetchMultiplePrices(tickers)  batched snapshot (preferred for many)
     - searchYahoo(query)            ticker/company autocomplete
                                     (kept by name for back-compat with
                                     existing TickerSearch consumers)
     - fetchBars(ticker, opts)       intraday OHLCV bars for charts
     - fetchSP500()                  live S&P 500 ticker list
     - PriceProvider, usePrices, TickerSearch, MarketStatus, TickerTape,
       FeedStatusBanner */
import { useState, useRef, useEffect, useCallback, createContext, useContext } from "react";
import { C } from "./_shared";

const PriceCtx = createContext({});
export const usePrices = () => useContext(PriceCtx);

const API = process.env.REACT_APP_BACKEND_URL || "";

export const BASE_TICKERS = [
  // Indices / volatility
  "SPY","QQQ","DIA","IWM","VIX",
  // Mega-cap tech
  "NVDA","AAPL","MSFT","AMZN","GOOG","META","TSLA","AMD","INTC","AVGO",
  // Financials
  "JPM","BAC","GS","V","MA",
  // Energy
  "XOM","CVX","OXY",
  // High-momentum / retail favourites
  "PLTR","CRWD","PANW","SNOW","COIN","MSTR","SMCI","IONQ",
  // Media / consumer
  "NFLX","DIS","SPOT",
];

async function _safeJson(url) {
  try {
    const res = await fetch(url, { credentials: "include", signal: AbortSignal.timeout(10000) });
    if (!res.ok) return null;
    return await res.json();
  } catch (e) {
    try { console.debug("[stock]", url, e?.message || e); } catch { /* noop */ }
    return null;
  }
}

// Single ticker. Always returns an object with `.ok` boolean.
export async function fetchPrice(ticker) {
  if (!ticker) return { ticker: "", ok: false, ts: Date.now() };
  const sym = String(ticker).toUpperCase();
  const data = await _safeJson(`${API}/api/stock/price/${encodeURIComponent(sym)}`);
  if (!data || !data.ok) return { ticker: sym, ok: false, ts: Date.now() };
  return { ...data, closes: [data.price], ts: Date.now() };
}

// Dedicated VIX fetcher — always uses the index endpoint (which proxies
// through VIXY since Polygon's free tier doesn't include the indices feed).
// Backend tries Alpaca real-time on VIXY first, falling back to Polygon
// snapshot. Kept separate from `fetchPrice` because (a) the bulk snapshot
// occasionally drops VIX during after-hours / Polygon flakes, leaving the
// ticker tape with "—", and (b) this guarantees the freshest VIXY tick
// regardless of the bulk endpoint's batching behavior.
export async function fetchVIX() {
  const data = await _safeJson(`${API}/api/stock/index/VIX`);
  if (!data || !data.ok) {
    return { ticker: "VIX", ok: false, ts: Date.now() };
  }
  return { ...data, closes: [data.price], ts: Date.now() };
}

// Batched snapshot — far faster than N x fetchPrice. Returns { [ticker]: payload }.
export async function fetchMultiplePrices(tickers) {
  if (!Array.isArray(tickers) || tickers.length === 0) return {};
  const symbols = [...new Set(tickers.map(t => String(t).toUpperCase()))].join(",");
  const data = await _safeJson(`${API}/api/stock/snapshot?tickers=${encodeURIComponent(symbols)}`);
  const out = {};
  for (const r of (data?.results || [])) {
    out[r.ticker] = { ...r, closes: [r.price], ts: Date.now() };
  }
  return out;
}

// Ticker / company-name autocomplete. Returns array of { symbol, name, exchange, type }.
// (Function name preserved for backwards-compat with existing TickerSearch component.)
export async function searchYahoo(query) {
  if (!query || query.length < 1) return [];
  const data = await _safeJson(`${API}/api/stock/search?q=${encodeURIComponent(query)}`);
  return Array.isArray(data?.results) ? data.results : [];
}

// Intraday bars — defaults to 78 × 5-min bars (full 6.5h trading day).
export async function fetchBars(ticker, opts = {}) {
  if (!ticker) return [];
  const sym = String(ticker).toUpperCase();
  const params = new URLSearchParams({
    timespan:   opts.timespan   || "minute",
    multiplier: String(opts.multiplier || 5),
    limit:      String(opts.limit      || 78),
  });
  const data = await _safeJson(`${API}/api/stock/bars/${encodeURIComponent(sym)}?${params.toString()}`);
  return Array.isArray(data?.bars) ? data.bars : [];
}

// Live S&P-500-ish ticker list (broad active US common stocks).
export async function fetchSP500() {
  const data = await _safeJson(`${API}/api/stock/sp500`);
  return Array.isArray(data?.tickers) ? data.tickers : [];
}

// Top-N most-active US stocks by today's $-volume.
//   limit:    e.g. 1000 or 2000
//   minPrice: floor (default 5 → no penny stocks)
//   minVol:   floor day-volume in shares (default 500k → no illiquid names)
// Backend caches the ranked list per ET trading day so repeat calls return
// instantly from the in-process cache.
export async function fetchMostActive(limit = 1000, minPrice = 5, minVol = 500_000) {
  const params = new URLSearchParams({
    limit:     String(limit),
    min_price: String(minPrice),
    min_vol:   String(minVol),
  });
  const data = await _safeJson(`${API}/api/stock/tickers/most-active?${params.toString()}`);
  return {
    tickers:     Array.isArray(data?.tickers) ? data.tickers : [],
    totalRanked: data?.total_ranked || 0,
    etDate:      data?.et_date || "",
  };
}

// Vault tickers (the user's curated long-term watchlist).
export async function fetchVaultTickers() {
  const data = await _safeJson(`${API}/api/trading/vault`);
  if (!Array.isArray(data)) return [];
  return data.map(s => s?.ticker).filter(Boolean).map(t => String(t).toUpperCase());
}

export function PriceProvider({ children }) {
  const [prices,    setPrices]    = useState({});
  const [loading,   setLoading]   = useState(false);
  const [lastUpd,   setLastUpd]   = useState(null);
  const [watchlist, setWatchlist] = useState(BASE_TICKERS);
  const [pendingTickers, setPendingTickers] = useState(new Set());

  // ── Feed status: tracks the live-feed health.
  //   "checking" → first fetch hasn't completed yet
  //   "live"     → most recent refresh produced at least one valid price
  //   "down"     → backend / market data unreachable for the most recent refresh
  const [feedStatus, setFeedStatus] = useState("checking");
  // ── Feed source: which upstream produced the most recent price.
  //   "alpaca-iex" / "alpaca-sip" → real-time feed (green pill)
  //   "polygon-delayed"            → fallback delayed feed (amber pill)
  //   ""                           → not yet probed
  const [feedSource, setFeedSource] = useState("");

  const refresh = useCallback(async (tickers = watchlist) => {
    setLoading(true);
    setPendingTickers(new Set(tickers));
    // Split VIX out of the bulk batch — its own dedicated endpoint
    // (/api/stock/index/VIX) hits Alpaca real-time on VIXY first and is
    // robust to bulk-snapshot flakes. Without this, Polygon's bulk endpoint
    // occasionally drops VIX during after-hours and the ticker tape shows "—".
    const wantsVIX = tickers.includes("VIX");
    const stockTickers = tickers.filter(t => t !== "VIX");
    if (wantsVIX) {
      // Fire-and-forget — VIX state is committed independently of the bulk path.
      fetchVIX().then(d => {
        if (d && d.ok) {
          setPrices(p => ({ ...p, VIX: d }));
        } else {
          setPrices(p => p.VIX?.ok ? { ...p, VIX: { ...p.VIX, stale: true } } : { ...p, VIX: { ticker: "VIX", ok: false, ts: Date.now() } });
        }
        setPendingTickers(prev => { const n = new Set(prev); n.delete("VIX"); return n; });
      });
    }
    // Polygon Starter is unlimited — pull EVERYTHING in one batched snapshot.
    // Snapshot endpoint accepts ~250 tickers per call; we cap at 200 for safety
    // and split into chunks if the watchlist grows beyond that.
    const CHUNK = 200;
    // Stream chunks into state as they arrive — first batch shows up
    // immediately even if subsequent chunks are still in flight.
    for (let i = 0; i < stockTickers.length; i += CHUNK) {
      const chunk = stockTickers.slice(i, i + CHUNK);
      let part = {};
      try {
        part = await fetchMultiplePrices(chunk);
      } catch (e) {
        try { console.debug("[refresh chunk]", e?.message || e); } catch { /* noop */ }
      }
      // Defensive client-side fallback: if the bulk call returned nothing for
      // a chunk, fan out per-ticker /price/{sym} calls in parallel. Backend
      // already does this, but this protects against partial outages and
      // makes Market Watch render whatever it can as soon as possible.
      const missing = chunk.filter(t => !part[t]);
      if (missing.length > 0 && Object.keys(part).length === 0) {
        try {
          const fallbacks = await Promise.all(missing.map(t => fetchPrice(t)));
          for (const d of fallbacks) {
            if (d && d.ok) part[d.ticker] = { ...d, closes: [d.price], ts: Date.now() };
          }
        } catch (e) {
          try { console.debug("[refresh fallback]", e?.message || e); } catch { /* noop */ }
        }
      }
      setPrices(p => {
        const next = { ...p };
        for (const t of chunk) {
          if (part[t]) next[t] = part[t];
          else if (next[t]?.ok) next[t] = { ...next[t], stale: true };
          else next[t] = { ticker: t, ok: false, ts: Date.now() };
        }
        return next;
      });
      setPendingTickers(prev => {
        const n = new Set(prev);
        for (const t of chunk) n.delete(t);
        return n;
      });
    }
    setLastUpd(new Date());
    setLoading(false);
  }, [watchlist]);

  // Dedicated feed health probe — uses the simple /api/stock/price/AAPL
  // endpoint rather than the bulk /snapshot endpoint. Snapshot can return
  // empty during after-hours or rate-limit blips even when individual price
  // lookups still succeed; the user-facing live-feed pill should reflect
  // the simpler health signal so it doesn't false-alarm. We also read the
  // `source` field so the pill can render "Alpaca" vs "Polygon delayed".
  useEffect(() => {
    let cancelled = false;
    const probe = async () => {
      try {
        const data = await _safeJson(`${API}/api/stock/price/AAPL`);
        if (cancelled) return;
        setFeedStatus(data?.ok === true ? "live" : "down");
        setFeedSource(data?.source || "");
      } catch {
        if (!cancelled) { setFeedStatus("down"); setFeedSource(""); }
      }
    };
    probe();
    const id = setInterval(probe, 30 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const addTicker = useCallback(t => {
    const tk = String(t).toUpperCase();
    setWatchlist(w => w.includes(tk) ? w : [...w, tk]);
    setPendingTickers(prev => new Set(prev).add(tk));
    fetchPrice(tk).then(d => {
      setPrices(p => ({ ...p, [tk]: d }));
      setPendingTickers(prev => { const n = new Set(prev); n.delete(tk); return n; });
    });
  }, []);

  const removeTicker = useCallback(t => {
    const tk = t.toUpperCase();
    setWatchlist(w => w.filter(x => x !== tk));
  }, []);

  // Initial fetch on mount.
  useEffect(() => {
    refresh();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  // Auto-refresh every 10s — Polygon Starter is unlimited so we can be aggressive.
  const watchlistRef = useRef(watchlist);
  useEffect(() => { watchlistRef.current = watchlist; }, [watchlist]);
  useEffect(() => {
    const id = setInterval(() => { refresh(watchlistRef.current); }, 10000);
    return () => clearInterval(id);
  }, [refresh]);

  return (
    <PriceCtx.Provider value={{ prices, loading, lastUpd, refresh, watchlist, addTicker, removeTicker, pendingTickers, feedStatus, feedSource }}>
      {children}
    </PriceCtx.Provider>
  );
}

// Banner shown only when the backend / Polygon feed is fully unreachable.
// In normal operation (feedStatus === "live") this renders nothing. The
// "🟢 Live · updated Xs ago" indicator lives in the TickerTape header strip.
export function FeedStatusBanner() {
  const { feedStatus, refresh } = usePrices();
  if (feedStatus !== "down") return null;
  return (
    <div
      data-testid="feed-status-banner"
      style={{
        background: "#fee2e2",
        borderBottom: "1px solid #fca5a5",
        color: "#991b1b",
        padding: "8px 16px",
        fontSize: 12,
        fontWeight: 600,
        display: "flex", alignItems: "center", gap: 12, justifyContent: "space-between",
      }}
    >
      <span>🔴 Live prices unavailable — both Alpaca and Polygon feeds unreachable. Check the backend or your network.</span>
      <button
        onClick={() => refresh()}
        data-testid="feed-status-retry"
        style={{
          background: "#7f1d1d", color: "#fff", border: "none",
          padding: "4px 12px", borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: "pointer",
        }}
      >
        Retry
      </button>
    </div>
  );
}

export function TickerSearch({ onSelect, placeholder = "Search ticker or company name…", autoFocus = false, width }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(0);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 1) {
      setResults([]); setOpen(false); return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      const r = await searchYahoo(q.trim());
      setResults(r); setOpen(r.length > 0); setHover(0); setLoading(false);
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const pick = (item) => {
    setQ(""); setResults([]); setOpen(false);
    onSelect && onSelect(item);
  };

  const onKey = (e) => {
    if (!open || results.length === 0) {
      if (e.key === "Enter" && q.trim()) {
        pick({ symbol: q.trim().toUpperCase(), name: q.trim().toUpperCase(), exchange: "", type: "EQUITY" });
      }
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setHover(h => Math.min(h+1, results.length-1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHover(h => Math.max(h-1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); pick(results[hover]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapRef} style={{ position:"relative", width: width || "100%" }}>
      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={onKey}
        onFocus={() => results.length > 0 && setOpen(true)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        data-testid="ticker-search"
        style={{
          width:"100%", padding:"9px 12px", border:`1px solid #d1d5db`, borderRadius:8,
          fontSize:13, outline:"none", background:"#fff",
        }}
      />
      {loading && (
        <span style={{ position:"absolute", right:10, top:9, fontSize:11, color:C.muted }}>Searching…</span>
      )}
      {open && results.length > 0 && (
        <div
          style={{
            position:"absolute", top:"100%", left:0, right:0, marginTop:4, zIndex:50,
            background:"#fff", border:`1px solid #d1d5db`, borderRadius:8,
            boxShadow:"0 6px 22px rgba(15,23,42,0.12)", maxHeight:280, overflowY:"auto",
          }}
        >
          {results.map((r, i) => (
            <div
              key={r.symbol+"_"+i}
              onMouseEnter={() => setHover(i)}
              onMouseDown={(e) => { e.preventDefault(); pick(r); }}
              style={{
                padding:"8px 12px", cursor:"pointer", display:"flex", alignItems:"center", gap:10,
                background: i===hover ? "#f1f5f9" : "transparent",
                borderBottom: i===results.length-1 ? "none" : `1px solid #d1d5db`,
              }}
              data-testid={`ticker-suggest-${r.symbol}`}
            >
              <span style={{ fontFamily:"ui-monospace, SFMono-Regular, monospace", fontWeight:700, fontSize:13, color:"#111", minWidth:64 }}>
                {r.symbol}
              </span>
              <span style={{ flex:1, fontSize:12, color:C.gray, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {r.name}
              </span>
              {r.exchange && (
                <span style={{ fontSize:10, color:C.muted, padding:"1px 6px", background:"#f8fafc", borderRadius:4 }}>{r.exchange}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function MarketStatus() {
  const now = new Date();
  const et  = new Date(now.toLocaleString("en-US", { timeZone:"America/New_York" }));
  const h=et.getHours(), m=et.getMinutes(), day=et.getDay();
  const mins=h*60+m, wd=day>=1&&day<=5;
  let label, color;
  if (!wd||mins<4*60||mins>=20*60)          { label="Market Closed"; color=C.red; }
  else if (mins<9*60+30)                     { label="Pre-Market";    color=C.amber; }
  else if (mins>=9*60+30&&mins<11*60)        { label="🟢 Open Session — Trade"; color=C.green; }
  else if (mins>=11*60&&mins<15*60)          { label="⚠ Mid-Session — Avoid";  color=C.amber; }
  else if (mins>=15*60&&mins<16*60)          { label="🟢 Power Hour — Trade";  color=C.green; }
  else                                        { label="After Hours";  color=C.purple; }
  return <div style={{ display:"flex", alignItems:"center", gap:5, background:color+"22", border:`1px solid ${color}44`, borderRadius:20, padding:"3px 11px" }}>
    <div style={{ width:6, height:6, borderRadius:"50%", background:color }}/>
    <span style={{ fontSize:11, color, fontWeight:600 }}>{label}</span>
  </div>;
}

export function TickerTape() {
  const { prices, lastUpd, pendingTickers, feedStatus, feedSource } = usePrices();
  const tickers = ["SPY","QQQ","VIX","NVDA","AAPL","META","TSLA","AMD","MSFT","AMZN"];
  const [tick, setTick] = useState(0);
  useEffect(() => { const id = setInterval(()=>setTick(t=>t+1), 1000); return ()=>clearInterval(id); }, []);
  const agoStr = lastUpd
    ? (() => {
        const s = Math.floor((Date.now() - lastUpd.getTime())/1000);
        if (s < 2)    return "just now";
        if (s < 60)   return `${s}s ago`;
        if (s < 3600) return `${Math.floor(s/60)}m ago`;
        return lastUpd.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"});
      })()
    : "loading…";
  void tick;
  const isLive = feedStatus === "live";
  // Pill semantics, per data source:
  //   alpaca-iex / alpaca-sip → 🟢 Live · Alpaca (real-time, green)
  //   polygon-delayed         → 🟡 Polygon · Delayed (fallback, amber)
  //   <down>                  → 🔴 Offline (red)
  //   <checking>              → 🟡 Connecting (amber)
  const isAlpaca   = isLive && (feedSource || "").startsWith("alpaca");
  const isPolygon  = isLive && feedSource === "polygon-delayed";
  let dotColor, dotLabel, sourceLabel;
  if (feedStatus === "down") {
    dotColor = "#ef4444"; dotLabel = "Offline"; sourceLabel = "no feed";
  } else if (!isLive) {
    dotColor = "#facc15"; dotLabel = "Connecting"; sourceLabel = "—";
  } else if (isAlpaca) {
    dotColor = "#22c55e"; dotLabel = "Live"; sourceLabel = "Alpaca";
  } else if (isPolygon) {
    dotColor = "#facc15"; dotLabel = "Polygon"; sourceLabel = "Delayed";
  } else {
    // Fallback (no source field at all) — assume Polygon legacy path.
    dotColor = "#22c55e"; dotLabel = "Live"; sourceLabel = "Polygon";
  }
  return (
    <div style={{ background:"#060f1e", padding:"5px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, overflowX:"auto", WebkitOverflowScrolling:"touch", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
      <div style={{ display:"flex", gap:0, overflowX:"auto", WebkitOverflowScrolling:"touch", flexShrink:1 }}>
        {tickers.map(t => {
          const d = prices[t];
          const isPending = pendingTickers && pendingTickers.has(t);
          if (!d || !d.ok) {
            return (
              <span key={t} style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"0 10px", color:C.dim, fontSize:10, borderRight:"1px solid rgba(255,255,255,0.06)" }}>
                <b>{t}</b>
                {isPending ? (
                  <span style={{ display:"inline-block", width:8, height:8, borderRadius:"50%", border:"1.5px solid rgba(255,255,255,0.4)", borderTopColor:"transparent", animation:"spin 0.8s linear infinite" }}/>
                ) : <span>···</span>}
              </span>
            );
          }
          const up = d.chg>=0;
          // VIX is shown via VIXY ETF on Starter plan — VIXY tracks
          // short-term VIX FUTURES at ~1.55× spot VIX, so the absolute
          // number is NOT the VIX index level. Show an "≈VIXY" hint and
          // a tooltip that explains the proxy + the approximate spot value.
          const proxyTitle = d._proxy
            ? `Approximate via ${d._proxy} ETF — not the actual ${t} index. Spot ${t} ≈ ${(d.price / 1.55).toFixed(1)}.`
            : (d.stale ? "Stale — last fetch failed" : "");
          return (
            <span key={t} style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"0 12px", borderRight:"1px solid rgba(255,255,255,0.06)", flexShrink:0, opacity: d.stale ? 0.5 : 1 }} title={d._note || proxyTitle}>
              <b style={{ fontSize:11, color:"#e2e8f0" }}>{t}{d._proxy ? <span style={{ fontSize:9, color:"#94a3b8", fontWeight:500 }}> (~{d._proxy})</span> : null}</b>
              <span style={{ fontSize:11, color:up?"#86efac":"#fca5a5", fontWeight:600 }}>${d.price.toFixed(2)}</span>
              <span style={{ fontSize:10, color:up?"#86efac":"#fca5a5", opacity:0.8 }}>{up?"▲":"▼"}{Math.abs(d.chgPct).toFixed(2)}%</span>
              {d._proxy && <span style={{ fontSize:9, color:"#fcd34d", marginLeft:1 }} title={proxyTitle}>≈</span>}
            </span>
          );
        })}
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0 }} data-testid="feed-status-pill">
        <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"2px 9px", borderRadius:20, background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)" }}>
          <span style={{ width:6, height:6, borderRadius:"50%", background:dotColor, boxShadow:isAlpaca?`0 0 6px ${dotColor}`:"none", animation:isAlpaca?"pulse 2s ease-in-out infinite":"none" }}/>
          <span data-testid="feed-source-label" style={{ fontSize:10, color:isAlpaca?"#86efac":dotColor, fontWeight:700, letterSpacing:"0.04em" }}>{dotLabel}</span>
          <span style={{ fontSize:10, color:C.dim }}>· {sourceLabel} · {agoStr}</span>
        </span>
      </div>
      <style>{`
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>
    </div>
  );
}
